import React, { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import pluralize from 'pluralize';

import { getPrintPackageItemListingLabel } from './helpers';
import {
  getPrintPackageGroupedItems,
  sortPrintPackageItems,
} from '../../../../../../../../../util/print-packages';
import { formatPriceForDisplay } from '../../../../../../../../../util/format-money';
import { getAlbumPrintPackageTaggedItem } from '../../helpers';
import {
  CURRENCY,
  getCurrencySymbol,
} from '../../../../../../../../../constants/currency';

const propTypes = {
  className: PropTypes.string,
  albumPrintPackage: PropTypes.shape({
    id: PropTypes.string.isRequired,
    print_package: PropTypes.shape({
      items: PropTypes.shape({
        total: PropTypes.number.isRequired,
        records: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string.isRequired,
            is_optional: PropTypes.bool,
            type: PropTypes.shape({
              name: PropTypes.string.isRequired,
            }),
            custom_print: PropTypes.shape({
              id: PropTypes.string.isRequired,
              name: PropTypes.string.isRequired,
            }),
            print_product_configuration: PropTypes.shape({
              print_product: PropTypes.shape({
                id: PropTypes.string.isRequired,
                name: PropTypes.string.isRequired,
              }).isRequired,
              print_product_size: PropTypes.shape({
                id: PropTypes.string.isRequired,
                print_size: PropTypes.shape({
                  id: PropTypes.string.isRequired,
                  name: PropTypes.string.isRequired,
                }).isRequired,
              }).isRequired,
            }),
          }).isRequired,
        ).isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
};

const PrintPackageItemsList = ({ className, albumPrintPackage }) => {
  const printPackage = albumPrintPackage.print_package;
  const printPackageItems = sortPrintPackageItems(printPackage.items.records);

  // Remove all optional items from the list as we don't want to show them.
  const filteredPrintPackageItems = printPackageItems.filter(
    printPackageItem =>
      printPackageItem.album_folder_print_package_items.total === 0,
  );
  const groupedPrintPackageItems = useMemo(
    () => getPrintPackageGroupedItems(filteredPrintPackageItems),
    [filteredPrintPackageItems],
  );
  const overriddenPrintPackageItem =
    getAlbumPrintPackageTaggedItem(albumPrintPackage);

  const albumFolderTag = overriddenPrintPackageItem?.album_folder_tag;
  const price = overriddenPrintPackageItem?.price?.value ?? 0;
  const discountPrice = overriddenPrintPackageItem?.discount_price?.value ?? 0;
  const finalPrice = discountPrice || price;
  const currencySymbol = getCurrencySymbol(
    overriddenPrintPackageItem?.discount_price?.currency ??
      overriddenPrintPackageItem?.price?.currency ??
      CURRENCY.USD,
  );
  const classes = classNames({
    [className]: !!className,
    'album-print-package-option-items': true,
    'one-column': true,
  });

  return (
    <ul className={classes}>
      {Object.keys(groupedPrintPackageItems).map(printPackageItemGroupKey => {
        const printPackageItemGroup =
          groupedPrintPackageItems[printPackageItemGroupKey];

        const [printPackageItem] = printPackageItemGroup;

        if (printPackageItemGroup?.length > 1) {
          return (
            <li key={printPackageItemGroup.id}>
              ({printPackageItemGroup.length}){' '}
              {getPrintPackageItemListingLabel(printPackageItem, true)}
            </li>
          );
        }

        return (
          <li key={printPackageItemGroup.id}>
            {getPrintPackageItemListingLabel(printPackageItem)}
          </li>
        );
      })}

      {
        // TODO check what happens when this case is active
        overriddenPrintPackageItem && (
          <Fragment>
            {overriddenPrintPackageItem.is_optional ? (
              <li>
                <strong>
                  Add{' '}
                  {pluralize(
                    albumFolderTag?.name ?? '',
                    overriddenPrintPackageItem.quantity,
                  )}{' '}
                  {getPrintPackageItemListingLabel(overriddenPrintPackageItem)}{' '}
                  for {currencySymbol}
                  {formatPriceForDisplay(finalPrice)}
                </strong>
              </li>
            ) : (
              <li>
                <strong>
                  Includes{' '}
                  {pluralize(
                    albumFolderTag?.name ?? '',
                    overriddenPrintPackageItem.quantity,
                  )}{' '}
                  {/* Since items from both sides are needed, allow the albumFolderPrintPackage to override */}
                  {getPrintPackageItemListingLabel(overriddenPrintPackageItem)}
                </strong>
              </li>
            )}
          </Fragment>
        )
      }
    </ul>
  );
};

PrintPackageItemsList.propTypes = propTypes;

export default PrintPackageItemsList;
